









































































import { Prop, Component, Vue, Watch } from 'vue-property-decorator';
import { EFieldType } from '@lib-shared/fields/types/enums/field-type';
import { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { ETooltipPlacement } from '@/enums/tooltip-placement';
import SignatureField from '@signing-page/fields/components/SignatureField.vue';
import InitialsField from '@signing-page/fields/components/InitialsField.vue';
import CheckboxField from '@signing-page/fields/components/CheckboxField.vue';
import DropdownField from '@signing-page/fields/components/DropdownField.vue';
import MergeField from '@signing-page/fields/components/MergeField.vue';
import LinkField from '@signing-page/fields/components/LinkField.vue';
import TextField from '@signing-page/fields/components/TextField.vue';
import { FieldHelper } from '@signing-page/fields/helpers/field.helper';
import { IUser } from '@signing-page/users/types/user';
import { ITooltip } from '@/types/tooltip';
import { IField } from '@lib-shared/fields/types/field';
import { IRecipient } from '@lib-shared/types/Recipient';
import { EFieldSource } from '@lib-shared/fields/types/enums/field-source';

@Component({
  components: {
    InitialsField,
    CheckboxField,
    MergeField,
    LinkField,
    TextField,
    DropdownField,
    SignatureField,
  },
  data: () => ({
    EFieldType,
  }),
})
export default class Field extends Vue {
  @Prop() field!: IField;
  @Prop() recipient: IRecipient;
  @Prop() allRecipients: IRecipient[];
  @Prop() sender: IUser;
  @Prop() focusedRequiredId?: string;

  @Prop() isDisabled!: boolean;
  @Prop() requiredStepsLeft!: boolean;
  @Prop() isSigned!: boolean;
  @Prop() initials: string;
  @Prop() verifiedInitialFieldIds: string[];

  active = false;
  focus = false;
  pressSpace = false;

  @Watch('focusedRequiredId')
  watchFocusedRequiredId(focusedRequiredId: string) {
    if (this.field.id === focusedRequiredId) {
      this.setFocus(true);
    } else {
      this.setFocus(false);
    }
  }

  get errorMessage(): string {
    return this.field.validationWarning || this.$t('invalid-field-value-entered').toString();
  }

  get tabIndex(): number {
    if (this.disabled || !FieldHelper.isTabbableField(this.field.type)) {
      return undefined;
    }
    return 0;
  }

  get fontSize(): string {
    return `${(this.field.position.height * 0.6).toFixed(1)}px`;
  }

  get top(): string {
    return `${this.field.position.top}px`;
  }

  get left(): string {
    return `${this.field.position.left}px`;
  }

  get height(): string {
    let fieldHeight = `${this.field.position.height}px`;
    if (!this.shouldGrow || !this.field.value) {
      return fieldHeight;
    }
    if (!this.isConnectedToCurrentRecipient) {
      fieldHeight = 'auto';
    }
    return fieldHeight;
  }

  get width(): string {
    if (this.field.type === EFieldType.Initials) {
      return 'auto';
    }
    return `${this.field.position.width}px`;
  }

  get minWidth(): string {
    if (this.field.type === EFieldType.Initials) {
      return `${this.field.position.width}px`;
    }
    return 'auto';
  }

  get fieldType(): string {
    return `${this.field.type}-field`;
  }

  get fieldSource(): string {
    return this.field.source === EFieldSource.Sender ? 'sender-field' : 'recipient-field';
  }

  get pageType(): string {
    return 'signing-page-field';
  }

  get completed(): boolean {
    return FieldHelper.fieldCompleted(this.field, this.verifiedInitialFieldIds);
  }

  get disabled(): boolean {
    if (this.isDisabled) {
      return true;
    }

    if (
      this.field.source === EFieldSource.Sender ||
      (this.recipient &&
        (this.recipient.status === ERecipientStatus.Signed ||
          this.recipient.status === ERecipientStatus.Rejected))
    ) {
      return true;
    }
    return (
      FieldHelper.isInputField(this.field.type) && this.field.recipientId !== this.recipient.id
    );
  }

  get tooltip(): ITooltip {
    if (this.showTooltip) {
      return {
        content: this.tooltipContent,
        offset: 10,
        placement: ETooltipPlacement.Top,
      };
    }
    return null;
  }

  get showTooltip(): boolean {
    if (this.field.source === EFieldSource.Sender) {
      return false;
    }
    return FieldHelper.showFieldTooltip(this.field.type, this.disabled, this.requiredStepsLeft);
  }

  get invalid(): boolean {
    if (this.field.type !== EFieldType.Formatted) {
      return false;
    }
    return this.field.value && !new RegExp(this.field.validationRegex).test(this.field.value);
  }

  get tooltipContent(): string {
    if (this.field.source === EFieldSource.Sender) {
      return this.$t('field-assigned-to-you').toString();
    }
    if (this.field.type === EFieldType.Signature && !this.disabled && this.requiredStepsLeft) {
      return this.$t('required-steps-remaining-before-sign').toString();
    }
    return `${this.$t('field-assigned-to').toString()} ${this.fullName}`;
  }

  get fullName(): string {
    switch (this.field.source) {
      case EFieldSource.Recipient:
        return !!this.fieldRecipient && this.fieldRecipient.fullName;
      case EFieldSource.Sender:
        return !!this.sender && this.sender.fullName;
      default:
        return null;
    }
  }

  get fieldRecipient(): IRecipient {
    if (!this.field || !this.field.recipientId) {
      return null;
    }
    return this.allRecipients.find(
      (recipient: IRecipient) => recipient.id === this.field.recipientId
    );
  }

  get shouldGrow(): boolean {
    return [EFieldType.Text, EFieldType.Formatted, EFieldType.Merge].includes(this.field.type);
  }

  get isTextBasedField(): boolean {
    return this.field.type === EFieldType.Text || this.field.type === EFieldType.Formatted;
  }

  get isConnectedToCurrentRecipient(): boolean {
    return this.recipient?.id === this.field.recipientId;
  }

  handleInput(value: string) {
    this.$emit('input', value);
  }

  handleDropdownToggle(isOpen: boolean) {
    this.active = isOpen;
  }

  handleUpdate(value: string) {
    this.$emit('update', { fieldId: this.field.id, value });
  }

  setFocus(hasFocus: boolean): void {
    if (this.disabled) {
      return;
    }
    this.focus = hasFocus;
  }

  setPressSpace(event, press: boolean): void {
    if (this.field.type === EFieldType.Check) {
      event.preventDefault();
      this.pressSpace = press;
    }
  }

  handleInitialsClick() {
    if (this.disabled) {
      return;
    }
    this.$emit('clickInitialsField', this.field.id);
  }
  handleClickLink() {
    if (this.field.value) {
      window.open(this.field.value.includes('http') ? this.field.value : `//${this.field.value}`);
    }
    this.$emit('clickLinkField', this.field.id);
  }

  handleSignature() {
    this.$emit('clickSignField');
  }
}
